import React, { ReactNode } from 'react';
import ReactHtmlParser from 'react-html-parser';
import { useIntl } from 'react-intl';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';

import RichText from '../../UI/RichText';
import Seo from '../../Seo/Seo';
import TextLink from '../../UI/TextLink/TextLink';
import Image from '../../UI/Image/Image';
import {
  AnnouncementPage,
} from '../../../ContentfulTypes';

import {
  Container,
  TitleContainer,
  Headline,
  ImageContainer,
  StyledButton,
} from './styles';

interface Props {
  contentfulData: AnnouncementPage;
}

const bodyOptions = {
  renderNode: {
    [BLOCKS.HEADING_1]: (_, children) => <h2>{children}</h2>,
    [BLOCKS.PARAGRAPH]: (_, children) => <p>{children}</p>,
    [BLOCKS.EMBEDDED_ENTRY]: (
      node,
      { locale } = useIntl(),
    ) => (
      <InlineEntry node={node} locale={locale} />
    ),
    [INLINES.EMBEDDED_ENTRY]: (
      node,
      { locale } = useIntl(),
    ) => (
      <InlineEntry node={node} locale={locale} />
    ),
  },
};

const AnnouncementScreen = ({ contentfulData }: Props) => (
  <>
    <Seo
      title={contentfulData?.metaTitle}
      description={contentfulData?.metaDescription?.metaDescription}
      noIndex
    />
    <Container>
      {contentfulData?.image && (
        <ImageContainer>
          <Image image={contentfulData?.image} />
        </ImageContainer>
      )}
      <TitleContainer>
        <Headline>{ReactHtmlParser(contentfulData?.heading?.heading)}</Headline>
      </TitleContainer>
      <RichText document={contentfulData?.body} options={bodyOptions} />
    </Container>
  </>
);

interface InlineEntryProps {
  node: ReactNode;
  locale: string;
}

const InlineEntry = ({ node, locale = 'en' }: InlineEntryProps) => {
  switch (node?.data?.target?.__typename) {
    case 'ContentfulCta':
      return node?.data?.target.ctaType === 'button' ? (
        <StyledButton
          key={node?.id}
          buttonText={node?.data?.target?.ctaLabel?.ctaLabel}
          buttonColour={node?.data?.target?.ctaColor}
          ariaLabel={node?.data?.target?.ctaAriaLabel}
          locale={locale}
          route={node?.data?.target?.ctaTarget}
          url={node?.data?.target?.ctaUrl}
        />
      ) : (
        <TextLink
          key={node?.id}
          route={node?.data?.target?.ctaTarget}
          linkText={node?.data?.target?.ctaLabel?.ctaLabel}
          locale={locale}
          url={node?.data?.target?.ctaUrl}
        />
      );
    case 'ContentfulDamAsset':
      return (
        <ImageContainer>
          <Image image={node?.data?.target} />
        </ImageContainer>
      );
    default:
      return null;
  }
};

export default AnnouncementScreen;
